import { clsx } from 'clsx';
import { useId, type ReactNode } from 'react';

export interface Tab {
  key: string;
  label: string;
  count?: number;
  hidden?: boolean;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  activeKey: string;
  onChange: (key: string) => void;
  children: ReactNode;
  variant?: 'default' | 'lender';
}

export default function Tabs({ tabs, activeKey, onChange, children, variant = 'default' }: TabsProps) {
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const enabledTabs = visibleTabs.filter((t) => !t.disabled);
  const id = useId();
  const isLender = variant === 'lender';

  return (
    <div>
      <div className={clsx('border-b', isLender ? 'border-border bg-transparent' : 'border-gray-200 dark:border-gray-700')}>
        <nav className={clsx('flex overflow-x-auto', isLender ? 'h-[34px] gap-6' : '-mb-px gap-4')} role="tablist" aria-label="Tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              id={`${id}-${tab.key}-tab`}
              role="tab"
              aria-selected={activeKey === tab.key}
              aria-disabled={tab.disabled || undefined}
              aria-controls={`${id}-panel`}
              tabIndex={activeKey === tab.key && !tab.disabled ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => onChange(tab.key)}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
                event.preventDefault();
                const index = enabledTabs.findIndex((item) => item.key === tab.key);
                const direction = event.key === 'ArrowRight' ? 1 : -1;
                const next = enabledTabs[(index + direction + enabledTabs.length) % enabledTabs.length];
                onChange(next.key);
                requestAnimationFrame(() => document.getElementById(`${id}-${next.key}-tab`)?.focus());
              }}
              className={clsx(
                'whitespace-nowrap border-b-2 transition-colors',
                isLender
                  ? 'h-[34px] shrink-0 p-0 font-sans text-[17px] font-semibold leading-5'
                  : 'py-2.5 px-1 text-sm font-medium',
                isLender
                  ? activeKey === tab.key
                    ? 'border-brand text-content'
                    : 'border-transparent text-content-secondary enabled:hover:text-content'
                  : activeKey === tab.key
                    ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 enabled:hover:border-gray-300 enabled:hover:text-gray-700 dark:text-gray-400 dark:enabled:hover:border-gray-600 dark:enabled:hover:text-gray-300',
                tab.disabled && 'cursor-default',
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={clsx(
                    'ml-2 rounded-full px-2 py-0.5 text-xs',
                    activeKey === tab.key
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${activeKey}-tab`} className={isLender ? 'pt-[30px]' : 'pt-4'}>{children}</div>
    </div>
  );
}
